import React from "react";
import { useLang, LANGUAGES } from "../lib/i18n";

/**
 * Multi-language picker — replaces the old EN | VI pill toggle.
 *
 * Uses a native `<select>` so we get free keyboard navigation, search-as-you-
 * type, and mobile-OS-native dropdowns on iOS/Android. Each option shows the
 * language's native name (so users find their own language even if the rest
 * of the UI is currently in a language they don't read).
 *
 * On change, the LanguageProvider's setLang triggers a full re-render via
 * context — every `useT()` consumer picks up the new translations.
 */
export default function LanguageToggle({ className = "", compact = false }) {
  const { lang, setLang } = useLang();
  return (
    <div
      className={[
        "inline-flex items-center rounded-full border border-[#E5E0D8] bg-[#FBF9F6]/70 backdrop-blur px-1.5 py-0.5",
        className,
      ].join(" ")}
      data-testid="lang-toggle"
    >
      <span aria-hidden className="text-[#4A5C50] mr-1 select-none" style={{ fontSize: "11px" }}>🌐</span>
      <select
        value={lang}
        onChange={(e) => setLang(e.target.value)}
        aria-label="Choose language"
        data-testid="lang-toggle-select"
        className={[
          "appearance-none bg-transparent mono tracking-wider text-[#184A30] cursor-pointer outline-none focus:outline-none border-0",
          "pr-4 pl-1 py-0",
          compact ? "text-[10px]" : "text-[11px]",
        ].join(" ")}
        style={{ backgroundPosition: "right 0px center", backgroundRepeat: "no-repeat" }}
      >
        {LANGUAGES.map((l) => (
          <option key={l.code} value={l.code} data-testid={`lang-option-${l.code}`}>
            {l.native}
          </option>
        ))}
      </select>
    </div>
  );
}
